import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { fmt, fmtDateShort } from '../lib/format';
import { UsersThree, Plus, Trash, PencilSimple } from '@phosphor-icons/react';

export default function Employees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get('/employees'); setItems(r.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const employees = items.filter(i => i.role === 'employee');
  const admins = items.filter(i => i.role === 'admin');

  const handleDelete = async (id) => {
    if (!confirm('确认删除该员工?')) return; // eslint-disable-line
    await api.delete(`/employees/${id}`); load();
  };

  return (
    <div className="space-y-5 fade-in" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">员工管理</h1>
          <p className="text-sm text-slate-400 mt-0.5">添加 / 管理员工账号</p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="add-employee-btn">
          <Plus size={16} /> 新增员工
        </button>
      </div>

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      {!loading && (
        <>
          <div>
            <div className="label-tiny mb-2">管理员 ({admins.length})</div>
            <div className="space-y-2">
              {admins.map(a => <UserCard key={a.id} u={a} />)}
            </div>
          </div>

          <div>
            <div className="label-tiny mb-2">员工 & 经理 ({employees.length})</div>
            {employees.length === 0 && (
              <div className="card-base p-8 text-center">
                <UsersThree size={32} weight="duotone" className="text-slate-700 mx-auto mb-2" />
                <p className="text-sm text-slate-500">还没有员工,点上方「新增」</p>
              </div>
            )}
            <div className="space-y-2">
              {employees.map(e => (
                <UserCard key={e.id} u={e}
                          onEdit={() => setEditing(e)}
                          onDelete={() => handleDelete(e.id)} />
              ))}
            </div>
          </div>
        </>
      )}

      {creating && <UserDialog onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
      {editing && <UserDialog user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function UserCard({ u, onEdit, onDelete }) {
  const roleLabel = u.role === 'admin' ? '管理员' : u.role === 'manager' ? '经理' : '员工';
  const roleColor = u.role === 'admin' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
    u.role === 'manager' ? 'bg-gradient-to-br from-cyan-500 to-blue-600' :
    'bg-gradient-to-br from-violet-500 to-indigo-600';
  const roleBadge = u.role === 'admin' ? 'bg-amber-500/15 text-amber-300 border-amber-500/30' :
    u.role === 'manager' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' :
    'bg-violet-500/15 text-violet-300 border-violet-500/30';
  return (
    <div className="card-base p-4 flex items-center justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${roleColor}`}>
          {u.name?.[0]}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="text-sm font-medium text-white truncate">{u.name}</div>
            <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded border ${roleBadge}`}>{roleLabel}</span>
          </div>
          <div className="text-xs text-slate-500 truncate">{u.email}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {u.role !== 'admin' && (
          <div className="text-right hidden sm:block">
            <div className="text-xs text-slate-500">月薪</div>
            <div className="font-num text-sm text-white">RM {fmt(u.monthly_salary)}</div>
          </div>
        )}
        {onEdit && <button onClick={onEdit} className="text-slate-400 hover:text-white p-2 rounded hover:bg-slate-800"><PencilSimple size={16} /></button>}
        {onDelete && <button onClick={onDelete} className="text-rose-400 hover:text-rose-300 p-2 rounded hover:bg-rose-500/10"><Trash size={16} /></button>}
      </div>
    </div>
  );
}

function UserDialog({ user, onClose, onDone }) {
  const isEdit = !!user;
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [salary, setSalary] = useState(user?.monthly_salary || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      if (isEdit) {
        const body = { name, monthly_salary: parseFloat(salary || 0) };
        if (password) body.password = password;
        await api.put(`/employees/${user.id}`, body);
      } else {
        await api.post('/employees', { email, name, password, monthly_salary: parseFloat(salary || 0) });
      }
      onDone();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card-base p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold text-white mb-4">{isEdit ? '编辑员工' : '新增员工'}</h3>
        <div className="space-y-3">
          <div>
            <label className="label-tiny block mb-1">姓名</label>
            <input className="input-base" placeholder="Boy" value={name} onChange={e => setName(e.target.value)} data-testid="emp-name" />
          </div>
          <div>
            <label className="label-tiny block mb-1">Email</label>
            <input className="input-base" placeholder="boy@xxx.com" value={email} disabled={isEdit}
                   onChange={e => setEmail(e.target.value)} data-testid="emp-email" />
          </div>
          <div>
            <label className="label-tiny block mb-1">{isEdit ? '新密码 (留空不修改)' : '密码'}</label>
            <input type="password" className="input-base" placeholder={isEdit ? '保留原密码留空' : '至少 6 位'}
                   value={password} onChange={e => setPassword(e.target.value)} data-testid="emp-password" />
          </div>
          <div>
            <label className="label-tiny block mb-1">月薪 (MYR)</label>
            <input type="number" step="0.01" className="input-base font-num" placeholder="5000"
                   value={salary} onChange={e => setSalary(e.target.value)} data-testid="emp-salary" />
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">取消</button>
            <button onClick={submit} disabled={loading} className="btn-primary flex-1" data-testid="save-employee">
              {loading ? '保存…' : isEdit ? '保存' : '创建'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
